import React, { useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { ShelfTypeFilterEnum } from '@soundxyz/utils/const';
import type { Maybe } from '@soundxyz/utils/helpers';
import { styled } from '@components-shared/src/stitches';
import { NewPlaylistCta } from '@components/Playlists/NewPlaylistCta';
import { LikedSoundsCard } from '@components/Playlists/basics/LikedSoundsCard';
import { PlaylistCard, PlaylistCardSkeleton } from '@components/Playlists/basics/PlaylistCard';
import { PlaylistFilters } from '@components/Playlists/basics/PlaylistFilters';
import {
  PLAYLISTS_COUNT,
  shouldShowLikedSounds,
  shouldShowPlaylistCreator,
} from '@components/Playlists/shared';
import {
  CarouselHeader,
  CarouselWithHeader,
} from '@components/main-components/carousel/HeaderCarousel';
import { DeprecatedDynamicSkeleton } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import type { PlaylistsCarouselPlaylistsQuery } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { useInfiniteQuery } from '@graphql/reactQuery';
import {
  PlaylistsCarouselPlaylistsDocument,
  PlaylistsCarouselUserInfoFragmentDoc,
} from '@graphql/reactQuery';
import { GRID_CAROUSEL_INITIAL_PAGE } from '@utils/constants';
import { formatProfileHeader } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistsCarouselUserInfo on User {
    id
    ...CarouselUserInfo
  }

  query PlaylistsCarouselPlaylists(
    $id: UUID!
    $pagination: ShelfCursorConnectionArgs!
    $filter: UserShelvesFilter!
  ) {
    user(id: $id) {
      id
      likedSounds {
        id
        releaseCount
        ...LikedSoundsCardDetails
      }
      shelvesCount(filter: $filter)

      shelves(pagination: $pagination, filter: $filter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            releaseCount
            ...PlaylistCardDetails
          }
          cursor
        }
      }
    }
  }
`);

export type PlaylistsCarouselSlidesType = NonNullable<
  PlaylistsCarouselPlaylistsQuery['user']
>['shelves']['edges'];

const SKELETON_CARDS = Array.from({ length: 4 }).map((_, i) => {
  return { id: `skeleton-${i}`, slide: <PlaylistCardSkeleton /> };
});

const NoPlaylists = ({
  subheader,
  user,
  playlistsType,
  isOwner,
}: {
  playlistsType: ShelfTypeFilterEnum;
  subheader: React.ReactNode;
  user: FragmentType<PlaylistsCarouselUserInfoFragmentDoc>;
  isOwner: boolean;
}) => {
  return (
    <>
      <CarouselHeader
        path="/playlists"
        header="Playlists"
        subheader={subheader}
        user={getFragment(PlaylistsCarouselUserInfoFragmentDoc, user)}
        seeAllParams={null}
      />
      <NewPlaylistCta type={playlistsType} isOwner={isOwner} />
    </>
  );
};

export const PlaylistsCarousel = ({
  user: userFragment,
  isOwner,
}: {
  userId: string;
  user: FragmentType<PlaylistsCarouselUserInfoFragmentDoc>;
  isOwner: Maybe<boolean>;
}) => {
  const [playlistsType, setPlaylistsType] = useState<ShelfTypeFilterEnum>(ShelfTypeFilterEnum.ALL);
  const { tablet } = MediaQuery.useContainer();

  const user = getFragment(PlaylistsCarouselUserInfoFragmentDoc, userFragment);

  const userId = user.id;

  const {
    orderedList: playlists,
    loadMoreNextPage: loadMore,
    firstPage: data,
    isLoading: loading,
  } = useInfiniteQuery(PlaylistsCarouselPlaylistsDocument, {
    filterQueryKey: {
      userId,
      playlistsType,
    },
    variables({ pageParam }) {
      return {
        id: userId,
        filter: {
          type: playlistsType,
        },
        pagination: {
          first: GRID_CAROUSEL_INITIAL_PAGE,
          sort: {
            createdAt: 'DESC' as const,
          },
          after: pageParam?.after || null,
        },
      };
    },
    getNextPageParam({ data }) {
      return (
        data.user?.shelves.pageInfo.hasNextPage && { after: data.user.shelves.pageInfo.endCursor }
      );
    },
    list({ user }) {
      if (isOwner) {
        return user?.shelves.edges.map(edge => edge.node);
      }

      return user?.shelves.edges.map(edge => edge.node).filter(node => node.releaseCount > 0);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  const likedSounds = data?.data?.user?.likedSounds;
  const hasLikedSounds = !!likedSounds && likedSounds.releaseCount > 0;
  const shelvesCount = data?.data?.user?.shelvesCount ?? 0;

  const playlistsSlides = React.useMemo(() => {
    // don't attempt to compute slides until we know if the user is the owner or not
    // Keen Slider does not respond well to appending slides after the initial render
    if (typeof isOwner === 'undefined') return;

    const slides = playlists?.map(playlist => ({
      id: playlist.id,
      slide: <PlaylistCard data={playlist} showMetadata />,
    }));

    const ownerSlide =
      isOwner && shouldShowPlaylistCreator(playlistsType)
        ? [
            {
              id: 'new',
              slide: (
                <NewPlaylistCta
                  type={playlistsType}
                  isOwner={isOwner}
                  card
                  hasPlaylists={slides.length > 0}
                />
              ),
            },
          ]
        : null;

    const likedSoundsSlide =
      hasLikedSounds && shouldShowLikedSounds(playlistsType)
        ? [
            {
              id: likedSounds.id,
              slide: <LikedSoundsCard playlistFrag={likedSounds} />,
            },
          ]
        : null;

    return [...(ownerSlide ?? []), ...(likedSoundsSlide ?? []), ...(slides ?? [])];
  }, [isOwner, playlists, likedSounds, hasLikedSounds, playlistsType]);

  const profileHeader = formatProfileHeader({
    count: PLAYLISTS_COUNT({
      type: playlistsType,
      shelfCount: shelvesCount,
      likedSound: hasLikedSounds,
    }),
    text: 'Playlists',
  });

  const subheader = (
    <SubheaderContainer>
      {loading ? (
        <DeprecatedDynamicSkeleton width={100} height={tablet ? 20 : 18} />
      ) : (
        profileHeader
      )}
      <PlaylistFilters
        playlistsType={playlistsType}
        onClick={type => {
          setPlaylistsType(type);
        }}
      />
    </SubheaderContainer>
  );

  if (loading && playlists.length === 0) {
    return (
      <CarouselWithHeader
        user={user}
        header="Playlists"
        subheader={subheader}
        slides={SKELETON_CARDS}
        path="/playlists"
        seeAllParams={
          playlistsType === ShelfTypeFilterEnum.ALL
            ? null
            : {
                playlistFilter: playlistsType.toLowerCase(),
              }
        }
        loadMore={loadMore}
      />
    );
  }

  // This is for when there are no slides to show
  if (!playlistsSlides || playlistsSlides.filter(slide => slide.id !== 'new').length === 0) {
    return (
      <NoPlaylists
        playlistsType={playlistsType}
        isOwner={isOwner || false}
        subheader={subheader}
        user={userFragment}
      />
    );
  }

  return (
    <CarouselWithHeader
      user={user}
      header="Playlists"
      subheader={subheader}
      slides={playlistsSlides}
      path="/playlists"
      seeAllParams={
        playlistsType === ShelfTypeFilterEnum.ALL
          ? null
          : {
              playlistFilter: playlistsType.toLowerCase(),
            }
      }
      loadMore={loadMore}
    />
  );
};

const SubheaderContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});
